import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Edit } from "lucide-react";
import { HeaderPage } from "@/components/HeaderPage";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useGetTeamById } from "@/hooks/useTeams";
import { useGetUsers } from "@/hooks/useUsers";
import { useGetCategories } from "@/hooks/useCategories";
import { useGetSectors } from "@/hooks/useSectors";
import { Loading } from "@/components/Loading";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const workShiftLabels: Record<string, string> = {
  MORNING: "Manhã",
  AFTERNOON: "Tarde",
  NIGHT: "Noite",
  FULL_TIME: "Integral",
};

const statusLabels: Record<string, string> = {
  ACTIVE: "Ativa",
  INACTIVE: "Inativa",
};

export const ViewTeam = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const { data: teamData, isLoading, error } = useGetTeamById(id!);
  const { data: usersData } = useGetUsers();
  const { data: categoriesData } = useGetCategories();
  const { data: sectorsData } = useGetSectors();

  const team = teamData?.data || teamData;

  if (isLoading) {
    return (
      <div className="flex flex-col h-full bg-gray-50">
        <HeaderPage
          title="Visualizar Equipe"
          description="Detalhes da equipe"
        />
        <div className="flex-1 flex items-center justify-center">
          <Loading label="Carregando equipe..." />
        </div>
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="flex flex-col h-full bg-gray-50">
        <HeaderPage
          title="Visualizar Equipe"
          description="Detalhes da equipe"
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-500 mb-4">
              {error ? "Erro ao carregar equipe" : "Equipe não encontrada"}
            </p>
            <Button onClick={() => navigate("/teams")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar para Equipes
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const leader =
    team?.leader ||
    usersData?.data?.find((user: any) => user.id === team?.leaderId);
  const sector =
    team?.sector ||
    sectorsData?.data?.find((s: any) => s.id === team?.sectorId);
  const members =
    team?.members ||
    usersData?.data?.filter((user: any) => team?.memberIds?.includes(user.id));
  const categories =
    team?.categories ||
    categoriesData?.data?.filter((cat: any) =>
      team?.categoryIds?.includes(cat.id),
    );

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <HeaderPage title="Visualizar Equipe" description="Detalhes da equipe" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 overflow-auto mt-10"
      >
        <div className="mx-auto">
          <Card className="bg-white">
            <CardContentUI className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Nome */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500">
                    Nome da Equipe
                  </label>
                  <p className="text-base font-semibold">{team?.name}</p>
                </div>

                {/* Setor */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500">
                    Setor
                  </label>
                  <p className="text-base font-semibold">
                    {sector?.name || team?.sector || team?.sectorId}
                  </p>
                </div>

                {/* Regional */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500">
                    Regional
                  </label>
                  <p className="text-base font-semibold">{team?.regional}</p>
                </div>

                {/* Líder */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500">
                    Líder
                  </label>
                  <p className="text-base font-semibold">
                    {leader?.firstName && leader?.lastName
                      ? `${leader.firstName} ${leader.lastName}`
                      : leader?.profile?.fullName ||
                        leader?.email ||
                        "Líder não encontrado"}
                  </p>
                </div>

                {/* Turno de Trabalho */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500">
                    Turno de Trabalho
                  </label>
                  <p className="text-base font-semibold">
                    {workShiftLabels[team?.workShift] || team?.workShift}
                  </p>
                </div>

                {/* Status */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500">
                    Status
                  </label>
                  <div>
                    <Badge
                      variant={
                        team?.status === "ACTIVE" ? "default" : "secondary"
                      }
                      className={
                        team?.status === "ACTIVE"
                          ? "bg-green-500"
                          : "bg-gray-500"
                      }
                    >
                      {statusLabels[team?.status] || team?.status}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Membros da Equipe */}
              <div className="mt-6 space-y-2">
                <label className="text-sm font-medium text-gray-500">
                  Membros da Equipe
                </label>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                        <TableHead>Email</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {members && members.length > 0 ? (
                        members.map((member: any) => (
                          <TableRow key={member.id}>
                            <TableCell className="font-medium">
                              {member.firstName && member.lastName
                                ? `${member.firstName} ${member.lastName}`
                                : member.profile?.fullName || "Sem nome"}
                            </TableCell>
                            <TableCell>{member.email}</TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            className="text-center text-gray-500"
                          >
                            Nenhum membro adicionado
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Categorias */}
              <div className="mt-6 space-y-2">
                <label className="text-sm font-medium text-gray-500">
                  Categorias
                </label>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nome</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categories && categories.length > 0 ? (
                        categories.map((category: any) => (
                          <TableRow key={category.id}>
                            <TableCell className="font-medium">
                              {category.name}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell className="text-center text-gray-500">
                            Nenhuma categoria adicionada
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/teams")}
                  className="cursor-pointer"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Voltar
                </Button>
                <Button
                  type="button"
                  onClick={() => navigate(`/teams/edit/${id}`)}
                  className="text-white cursor-pointer"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Editar
                </Button>
              </div>
            </CardContentUI>
          </Card>
        </div>
      </motion.div>
    </div>
  );
};
